import type { EventType, MessagePhase } from "@/api";

export const EVENT_TYPES: EventType[] = ["incident", "maintenance", "info"];

export const EVENT_TYPE_LABEL: Record<EventType, string> = {
  incident: "Störung",
  maintenance: "Wartung",
  info: "Info",
};

export const EVENT_STATUS_LABEL: Record<string, string> = {
  draft: "Entwurf",
  announced: "Angekündigt",
  reminded: "Erinnert",
  started: "Gestartet",
  updating: "Update",
  closed: "Abgeschlossen",
  cancelled: "Abgebrochen",
};

export const MESSAGE_PHASE_LABEL: Record<MessagePhase, string> = {
  announcement: "Ankündigung",
  reminder: "Erinnerung",
  start: "Beginn",
  update: "Update",
  close: "Abschluss-Mail",
  adhoc: "Info",
};

export const MESSAGE_PHASE_HINT: Record<MessagePhase, string> = {
  announcement: "Vorab, z. B. geplante Wartung",
  reminder: "Kurze Erinnerung vor dem Termin",
  start: "Es geht jetzt los / Störung läuft",
  update: "Neuer Stand zur laufenden Sache",
  close: "Schriftlicher Abschluss an die Kunden (optional)",
  adhoc: "Einzelne Info, kein Lebenszyklus",
};

export function phasesForType(type: EventType): MessagePhase[] {
  if (type === "incident") return ["start", "update", "close"];
  if (type === "info") return ["adhoc"];
  return ["announcement", "reminder", "start", "update", "close"];
}

export const MESSAGE_STATE_LABEL: Record<string, string> = {
  draft: "Entwurf",
  scheduled: "Geplant",
  sending: "Wird gesendet",
  sent: "Gesendet",
  failed: "Fehlgeschlagen",
  cancelled: "Abgebrochen",
  queued: "In Queue",
};

export const MESSAGE_STAT_LABEL: Record<string, string> = {
  draft: "Entworfen",
  scheduled: "Geplant",
  sending: "In Queue",
  sent: "Versandt",
  failed: "Fehlgeschlagen",
  cancelled: "Abgebrochen",
};

export const DELIVERY_STAT_LABEL: Record<string, string> = {
  queued: "Wartend",
  deferred_rate_limit: "Rate-Limit",
  deferred_scheduled: "Geplant",
  deferred_retry: "Retry",
  deferred_smtp: "SMTP nicht erreichbar",
  submitting: "Einlieferung",
  submitted: "SMTP-Annahme",
  rejected: "Abgelehnt (5xx)",
  failed: "Fehlgeschlagen",
  unknown: "Unbekannt",
  cancelled: "Abgebrochen",
};

export const BOUNCE_CATEGORY_LABEL: Record<string, string> = {
  invalid_address: "Adresse existiert nicht",
  mailbox_full: "Postfach voll",
  rejected: "abgelehnt (Richtlinie/Spam)",
  temporary: "vorübergehend, aufgegeben",
  other: "sonstiger Fehler",
};

export const BOUNCE_KIND_LABEL: Record<string, string> = {
  failed: "nicht zugestellt",
  delayed: "verzögert",
  delivered: "zugestellt gemeldet",
  auto_reply: "Abwesenheitsnotiz",
  unknown: "nicht erkannt",
};

export const BOUNCE_CATEGORY_ORDER = [
  "invalid_address",
  "mailbox_full",
  "rejected",
  "temporary",
  "other",
] as const;

export function bounceCategoryLabel(category: string | null | undefined): string {
  return category ? (BOUNCE_CATEGORY_LABEL[category] ?? category) : "—";
}

export const STAT_WINDOWS: { id: "24h" | "7d" | "30d" | "all"; label: string }[] = [
  { id: "24h", label: "24 h" },
  { id: "7d", label: "7 T" },
  { id: "30d", label: "30 T" },
  { id: "all", label: "Gesamt" },
];

export const PLACEHOLDER_KEYS = [
  "building",
  "provider",
  "date",
  "start",
  "end",
  "scope",
  "contact",
] as const;

export const PLACEHOLDER_LABEL: Record<(typeof PLACEHOLDER_KEYS)[number], string> = {
  building: "Gebäude",
  provider: "Netzbetreiber",
  date: "Datum",
  start: "Beginn",
  end: "Ende",
  scope: "Umfang",
  contact: "Kontakt",
};

export const EMPTY_PLACEHOLDERS: Record<string, string> = {
  building: "",
  provider: "",
  date: "",
  start: "",
  end: "",
  scope: "",
  contact: "",
};

export function defaultPhaseForType(type: EventType): MessagePhase {
  if (type === "incident") return "start";
  if (type === "info") return "adhoc";
  return "announcement";
}

export function statusTone(status: string): "muted" | "accent" | "warn" | "success" | "danger" {
  switch (status) {
    case "draft":
      return "muted";
    case "announced":
    case "reminded":
      return "accent";
    case "started":
    case "updating":
      return "warn";
    case "closed":
      return "success";
    case "cancelled":
      return "danger";
    default:
      return "muted";
  }
}

export function typeTone(type: EventType): "danger" | "warn" | "accent" {
  if (type === "incident") return "danger";
  if (type === "maintenance") return "warn";
  return "accent";
}

export function eventStatusLabel(event: {
  type: string;
  status: string;
  last_phase?: string | null;
  sent_count?: number;
}): string {
  const sent = (event.sent_count ?? 0) > 0;
  if (event.type === "info" && event.status === "closed" && sent) {
    return "Gesendet";
  }
  if (event.type === "info" && event.status === "announced") {
    return "Gesendet";
  }
  if (event.status === "announced" && event.last_phase === "adhoc") {
    return "Gesendet";
  }
  return EVENT_STATUS_LABEL[event.status] ?? event.status;
}

export function isOpenEvent(status: string): boolean {
  return status !== "draft" && status !== "closed" && status !== "cancelled";
}

export function lastMailLabel(phase: string | null | undefined, sentCount: number): string {
  if (!phase || sentCount < 1) return "Noch keine Mail";
  const phaseLabel = MESSAGE_PHASE_LABEL[phase as MessagePhase] ?? phase;
  if (sentCount === 1) return `${phaseLabel} gesendet`;
  return `${sentCount} Mails, zuletzt ${phaseLabel}`;
}

/** Old Mailman subjects carry "[Netzmeldungen.x] +++ DE +++ EN +++"; lists show the DE part. */
export function legacySubject(subject: string): string {
  const text = subject.replace(/\[netzmeldungen[^\]]*\]\s*/gi, "").trim();
  const parts = text
    .split("+++")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts[0] ?? text;
}
